import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import StepCard from "./StepCard";
import QuestionsForm from "./QuestionsForm";
import FeedbackModal from "./FeedbackModal";
import ReferralRewardModal from "../../components/ReferralRewardModal";
import Step3ClientPendingModal from "../../components/Step3ClientPendingModal";
import DocumentSigningModal from "./DocumentSigningModal";
import TourGuide from "../../components/TourGuide";
import { getQuestions, clearQuestionsCache } from "./questionDataService";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/SocketContext";
import { useRealtimeRefresh, usePortalCmsVersion } from "../../context/RealtimeSyncContext";
import {
  usePortalContent,
  getTaxFilingPageMeta,
  getStepCardMeta,
  mergeStepsWithPortal,
  getFormBadgeLabel,
} from "../../portal";
import { FaHistory, FaChevronDown, FaChevronUp, FaLock, FaUnlock, FaClock, FaTimes, FaClipboardCheck } from "react-icons/fa";
import { LuCircleHelp } from "react-icons/lu";
import { PageHeaderSkeleton, StepCardsSkeleton } from "../../components/ui/Skeleton";
import { toast } from "../../components/ui/Toast";

const StepTracker = () => {
  const { currentThemeColors } = useTheme();
  const { content: taxFilingContent } = usePortalContent('tax-filing');
  const taxFilingMeta = useMemo(() => getTaxFilingPageMeta(taxFilingContent), [taxFilingContent]);
  const defaultSteps = useMemo(() => mergeStepsWithPortal([], taxFilingMeta.stepCards), [taxFilingMeta.stepCards]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getFirstName = (fullName) => {
    if (!fullName) return '';
    return fullName.trim().split(/\s+/)[0];
  };

  const getDueDateSuffix = (form) => {
    if (form === '1040' || form === '1120') return '(3.5 months)';
    if (form === '1065' || form === '1120-S') return '(2.5 months)';
    return null;
  };

  const renderTaxDateInfo = (taxYearEndDate, dueDate, form) => {
    if (!taxYearEndDate && !dueDate) return null;
    return (
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-600 dark:text-gray-400 shrink-0 sm:justify-end">
        {taxYearEndDate && (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-secondary-500 shrink-0" />
            <span>
              Tax Year End: <strong className="text-dark-700 dark:text-gray-300">{formatDate(taxYearEndDate)}</strong>
            </span>
          </div>
        )}
        {dueDate && (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-primary-500 shrink-0" />
            <span>
              Due Date: <strong className="text-dark-700 dark:text-gray-300">{formatDate(dueDate)}</strong>
              {getDueDateSuffix(form) && (
                <span className="text-primary-600 dark:text-primary-400 font-medium ml-1">{getDueDateSuffix(form)}</span>
              )}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Helper function to calculate months difference
  const getMonthsDifference = (taxYearEndDate, dueDate) => {
    if (!taxYearEndDate || !dueDate) return null;
    const start = new Date(taxYearEndDate);
    const end = new Date(dueDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const months = (diffDays / 30).toFixed(1);
    return `${months} months`;
  };

  const [steps, setSteps] = useState(defaultSteps);

  const [memberSteps, setMemberSteps] = useState([]);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [memberFormVisible, setMemberFormVisible] = useState({});
  const [assignedForm, setAssignedForm] = useState("1040");
  const [memberForms, setMemberForms] = useState({});
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [memberTotalQuestions, setMemberTotalQuestions] = useState({});
  const [userData, setUserData] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [taxFilingLocked, setTaxFilingLocked] = useState(false);
  const [taxFilingHistory, setTaxFilingHistory] = useState([]);
  const [memberTaxFilingLocked, setMemberTaxFilingLocked] = useState({});
  const [memberTaxFilingHistory, setMemberTaxFilingHistory] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [showMemberHistory, setShowMemberHistory] = useState({});
  const [isHistorySidebarOpen, setIsHistorySidebarOpen] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [isRazorpayReady, setIsRazorpayReady] = useState(false);
  const [showStep3Modal, setShowStep3Modal] = useState(false);
  const [showMemberStep3Modal, setShowMemberStep3Modal] = useState({});
  const [step3CustomTable, setStep3CustomTable] = useState(null);
  const [memberStep3CustomTable, setMemberStep3CustomTable] = useState({});
  const [isSubmittingStep3, setIsSubmittingStep3] = useState(false);
  const [runTour, setRunTour] = useState(false);

  // Step 4 Document Signing states
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [signingDocUrl, setSigningDocUrl] = useState('');
  const [signingBoxes, setSigningBoxes] = useState([]);
  const [signingMemberIndex, setSigningMemberIndex] = useState(-1);
  const [feedbackMemberIndex, setFeedbackMemberIndex] = useState(-1);
  const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);

  useEffect(() => {
    if (isHistorySidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isHistorySidebarOpen]);

  const navigate = useNavigate();

  const getTaxUnlockPriceCents = useCallback((memberIndex = null) => {
    const fallback = 15000;
    if (!userData) return fallback;

    if (memberIndex !== undefined && memberIndex !== null && Number(memberIndex) >= 0) {
      const member = userData.members?.[Number(memberIndex)];
      const memberPrice = member?.taxFilingUnlockPriceCents;
      if (Number.isFinite(memberPrice) && memberPrice >= 100) return memberPrice;
    }

    const userPrice = userData.taxFilingUnlockPriceCents;
    if (Number.isFinite(userPrice) && userPrice >= 100) return userPrice;
    return fallback;
  }, [userData]);

  useEffect(() => {
    let script;
    const ensureRazorpayScript = () => {
      if (window.Razorpay) {
        setIsRazorpayReady(true);
        return;
      }

      script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => setIsRazorpayReady(true);
      script.onerror = () => {
        console.error("Failed to load Razorpay script");
        setIsRazorpayReady(false);
      };
      document.body.appendChild(script);
    };

    ensureRazorpayScript();

    return () => {
      if (script) {
        script.onload = null;
        script.onerror = null;
      }
    };
  }, []);

  const fetchUserData = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch user data");

      const data = await response.json();
      setUserData(data.user);
      setAssignedForm(data.user.assignedForm || "1040");

      // Initialize member forms
      const initialMemberForms = {};
      const initialMemberSteps = [];
      const initialMemberTotalQuestions = {};

      if (data.user.members) {
        for (let index = 0; index < data.user.members.length; index++) {
          const member = data.user.members[index];
          initialMemberForms[index] = member.assignedForm || "1040";
          if (member.steps && member.steps.length > 0) {
            // Normalize member steps: convert old 5-step structure to new 4-step structure
            let normalizedMemberSteps = member.steps;

            // If 5 steps (old structure), convert to 4 steps
            if (normalizedMemberSteps.length === 5) {
              normalizedMemberSteps = [
                {
                  ...normalizedMemberSteps[0],
                  step: 1,
                  title: "Initial Information Required",
                  files: normalizedMemberSteps[0]?.files || []
                },
                {
                  ...normalizedMemberSteps[1],
                  step: 2,
                  title: "Tax Return Preparation",
                  files: normalizedMemberSteps[1]?.files || []
                },
                {
                  step: 3,
                  title: "Review Draft Tax Return",
                  // Merge step 3 and step 4 status - use the more advanced one
                  status: normalizedMemberSteps[3]?.status === 'submitted' ? normalizedMemberSteps[3].status : (normalizedMemberSteps[2]?.status || "pending"),
                  progress: normalizedMemberSteps[3]?.progress || normalizedMemberSteps[2]?.progress || 0,
                  answers: { ...(normalizedMemberSteps[2]?.answers || {}), ...(normalizedMemberSteps[3]?.answers || {}) },
                  comments: { ...(normalizedMemberSteps[2]?.comments || {}), ...(normalizedMemberSteps[3]?.comments || {}) },
                  file: normalizedMemberSteps[3]?.file || normalizedMemberSteps[2]?.file || null,
                  files: [...(normalizedMemberSteps[2]?.files || []), ...(normalizedMemberSteps[3]?.files || [])]
                },
                {
                  ...normalizedMemberSteps[4],
                  step: 4,
                  title: "Return sent for Filing",
                  files: normalizedMemberSteps[4]?.files || []
                }
              ];
            }

            // Ensure all member steps have the files property for backward compatibility
            initialMemberSteps[index] = mergeStepsWithPortal(
              normalizedMemberSteps.map(step => ({
                ...step,
                files: step.files || [],
              })),
              taxFilingMeta.stepCards
            );
          } else {
            initialMemberSteps[index] = mergeStepsWithPortal([], taxFilingMeta.stepCards);
          }

          // Fetch questions for member form to calculate total
          try {
            const memberFormQuestions = await getQuestions(member.assignedForm || "1040");
            initialMemberTotalQuestions[index] = Object.values(memberFormQuestions).reduce(
              (total, section) => total + section.length,
              0
            );
          } catch (error) {
            console.error(`Error fetching questions for member ${index}:`, error);
            initialMemberTotalQuestions[index] = 0;
          }
        }
      }

      setMemberForms(initialMemberForms);
      console.log('Loading member steps with files:', initialMemberSteps);
      setMemberSteps(initialMemberSteps);
      setMemberTotalQuestions(initialMemberTotalQuestions);

      // Calculate total questions for primary form
      try {
        const formQuestions = await getQuestions(data.user.assignedForm || "1040");
        const total = Object.values(formQuestions).reduce((total, section) => total + section.length, 0);
        setTotalQuestions(total);
      } catch (error) {
        console.error('Error fetching primary form questions:', error);
        setTotalQuestions(0);
      }

      // Set steps from user data if available
      if (data.user.steps && data.user.steps.length > 0) {
        // Normalize steps: convert old 5-step structure to new 4-step structure
        let normalizedSteps = data.user.steps;

        // If 5 steps (old structure), convert to 4 steps
        if (normalizedSteps.length === 5) {
          normalizedSteps = [
            {
              ...normalizedSteps[0],
              step: 1,
              title: "Initial Information Required",
              files: normalizedSteps[0]?.files || []
            },
            {
              ...normalizedSteps[1],
              step: 2,
              title: "Tax Return Preparation",
              files: normalizedSteps[1]?.files || []
            },
            {
              step: 3,
              title: "Draft Tax Return",
              // Merge step 3 and step 4 status - use the more advanced one
              status: normalizedSteps[3]?.status === 'submitted' ? normalizedSteps[3].status : (normalizedSteps[2]?.status || "pending"),
              progress: normalizedSteps[3]?.progress || normalizedSteps[2]?.progress || 0,
              answers: { ...(normalizedSteps[2]?.answers || {}), ...(normalizedSteps[3]?.answers || {}) },
              comments: { ...(normalizedSteps[2]?.comments || {}), ...(normalizedSteps[3]?.comments || {}) },
              file: normalizedSteps[3]?.file || normalizedSteps[2]?.file || null,
              files: [...(normalizedSteps[2]?.files || []), ...(normalizedSteps[3]?.files || [])]
            },
            {
              ...normalizedSteps[4],
              step: 4,
              title: "Return sent for filing",
              files: normalizedSteps[4]?.files || []
            }
          ];
        }

        // Ensure all steps have the files property for backward compatibility
        const stepsWithFiles = normalizedSteps.map(step => ({
          ...step,
          files: step.files || []
        }));
        console.log('Loading steps with files:', stepsWithFiles);
        setSteps(mergeStepsWithPortal(stepsWithFiles, taxFilingMeta.stepCards));
      }

      // Set tax filing locked state and history
      setTaxFilingLocked(data.user.taxFilingLocked || false);
      setTaxFilingHistory(data.user.taxFilingHistory || []);

      // Set member locked states and history
      const memberLockedStates = {};
      const memberHistoryData = {};
      if (data.user.members) {
        data.user.members.forEach((member, index) => {
          memberLockedStates[index] = member.taxFilingLocked || false;
          memberHistoryData[index] = member.taxFilingHistory || [];
        });
      }
      setMemberTaxFilingLocked(memberLockedStates);
      setMemberTaxFilingHistory(memberHistoryData);

      // Show feedback modal if primary step 4 is completed but feedback not submitted
      const primaryStep4Done = data.user.steps?.some(s => s.step === 4 && s.status === 'submitted');

      if (primaryStep4Done && (!data.user.feedback || !data.user.feedback.rating)) {
        setFeedbackMemberIndex(-1);
        setShowFeedbackModal(true);
      } else {
        // Check if any member's step 4 is completed
        const memberIndexWithPendingFeedback = data.user.members?.findIndex(member =>
          member.steps?.some(s => s.step === 4 && s.status === 'submitted') &&
          (!member.feedback || !member.feedback.rating)
        );

        if (memberIndexWithPendingFeedback !== undefined && memberIndexWithPendingFeedback !== -1) {
          setFeedbackMemberIndex(memberIndexWithPendingFeedback);
          setShowFeedbackModal(true);
        }
      }
    } catch (error) {
      console.error(error);
      setError('Failed to load user data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate, taxFilingMeta.stepCards]);

  useEffect(() => {
    clearQuestionsCache();
  }, []);

  useEffect(() => {
    setSteps((prev) => mergeStepsWithPortal(prev, taxFilingMeta.stepCards));
    setMemberSteps((prev) => prev.map((memberStepList) => mergeStepsWithPortal(memberStepList, taxFilingMeta.stepCards)));
  }, [taxFilingMeta.stepCards]);

  useEffect(() => {
    fetchUserData();

    // Check if user has seen the tour or if explicitly started via URL
    const hasSeenTour = localStorage.getItem('hasSeenTaxFilingTour');
    const urlParams = new URLSearchParams(window.location.search);
    const shouldStartTour = urlParams.get('startTour') === 'true';

    if (shouldStartTour) {
      setRunTour(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } 
    // Commented out to stop tour from automatically starting
    // else if (!hasSeenTour) {
    //   setTimeout(() => setRunTour(true), 1500);
    // }
  }, [fetchUserData]);

  useRealtimeRefresh(['tax-filing', 'profile'], fetchUserData);

  const taxFilingCmsVersion = usePortalCmsVersion('tax-filing');
  useEffect(() => {
    if (taxFilingCmsVersion === 0) return;
    clearQuestionsCache();
  }, [taxFilingCmsVersion]);

  const handleTourFinish = () => {
    localStorage.setItem('hasSeenTaxFilingTour_v2', 'true');
  };

  // Helper to determine if all primary steps (1-4) are completed
  const primaryAllStepsCompleted = steps &&
    steps.length > 0 &&
    steps
      .filter(s => s.step <= 4)
      .every(s => s.status === 'submitted' || s.status === 'completed');

  const handleStepClick = async (stepNumber, memberType = "primary", memberIndex = 0) => {
    // Check if locked
    if (memberType === "primary" && taxFilingLocked) {
      toast.warning("Tax filing is locked. Please pay to unlock.");
      return;
    }
    if (memberType === "member" && memberTaxFilingLocked[memberIndex]) {
      toast.warning("Tax filing is locked. Please pay to unlock.");
      return;
    }

    if (stepNumber === 4) {
      const stepsToCheck = memberType === "primary" ? steps : memberSteps[memberIndex] || [];

      // FIRST: Check if Step 4 has a signature document waiting to be signed
      const step4 = stepsToCheck.find(s => s.step === 4);
      if (step4 && step4.status === 'client-pending' && step4.signatureDocument && step4.signatureDocument.fileUrl && !step4.signatureDocument.signedFileUrl) {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
        setSigningDocUrl(`${apiBase}${step4.signatureDocument.fileUrl}`);
        setSigningBoxes(step4.signatureDocument.signatureBoxes || []);
        setSigningMemberIndex(memberType === 'member' ? memberIndex : -1);
        setShowSigningModal(true);
        return;
      }

      // THEN: Check if all previous steps are submitted before navigating to documents
      const allPreviousStepsCompleted = stepsToCheck
        .filter(step => step.step < 4)
        .every(step => step.status === 'submitted' || step.status === 'completed');

      if (!allPreviousStepsCompleted) {
        toast.warning('Please complete all previous steps before accessing the Documents section.');
        return;
      }

      navigate("/documents");
      return;
    }

    if (stepNumber === 1) {
      if (memberType === "primary") {
        if (steps[0].status === "submitted") {
          toast.warning("Step 1 has already been submitted and cannot be reopened.");
          return;
        }
        setIsFormVisible(true);
      } else {
        if (memberSteps[memberIndex] && memberSteps[memberIndex][0].status === "submitted") {
          toast.warning(`Step 1 has already been submitted and cannot be reopened.`);
          return;
        }
        setMemberFormVisible(prev => ({ ...prev, [memberIndex]: true }));
      }
    }

    // Handle Step 3 client-pending status
    if (stepNumber === 3) {
      const currentSteps = memberType === "primary" ? steps : memberSteps[memberIndex] || [];
      const step3 = currentSteps.find(s => s.step === 3);

      if (step3 && step3.status === 'client-pending') {
        // Fetch Step 3 custom table data
        const token = localStorage.getItem("token");
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/auth/step3-custom-table?userId=${userData._id}&memberIndex=${memberType === 'member' ? memberIndex : ''}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            if (memberType === "primary") {
              setStep3CustomTable(data.customTable);
              setShowStep3Modal(true);
            } else {
              setMemberStep3CustomTable(prev => ({ ...prev, [memberIndex]: data.customTable }));
              setShowMemberStep3Modal(prev => ({ ...prev, [memberIndex]: true }));
            }
          } else {
            toast.error('Failed to load Step 3 form. Please try again.');
          }
        } catch (error) {
          console.error('Error fetching Step 3 custom table:', error);
          toast.error('Error loading Step 3 form.');
        }
      }
    }

  };

  // Handle payment for unlocking tax filing - redirect to pricing page
  const handlePayToUnlock = (memberType = "primary", memberIndex = null) => {
    navigate('/pricing?service=service_2&step=1.5');
  };

  const handleFormSubmit = async (data, isFinalSubmit, memberType = "primary", memberIndex = 0) => {
    const answeredCount = Object.keys(data.answers).filter(
      (k) => data.answers[k] !== undefined && data.answers[k] !== null && data.answers[k] !== ''
    ).length;

    if (memberType === "primary") {
      const updatedSteps = steps.map((s) => {
        if (s.step === 1) {
          return {
            ...s,
            title: s.title,
            status: isFinalSubmit ? "submitted" : answeredCount >= 0 ? "in-progress" : "pending",
            progress: answeredCount,
            answers: data.answers,
            comments: data.comments,
            file: data.file || s.file,
            files: data.files || s.files || [],
            signatureData: isFinalSubmit ? data.signatureData : null,
          };
        }
        if (s.step === 2 && isFinalSubmit) {
          return {
            ...s,
            title: s.title,
            status: "in-progress",
          };
        }
        return s;
      });

      setSteps(updatedSteps);

      // Save the updated steps to the database
      const token = localStorage.getItem("token");
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-steps`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            steps: updatedSteps,
            signatureData: isFinalSubmit ? data.signatureData : null
          }),
        });

        if (!response.ok) throw new Error("Failed to update steps");

        if (isFinalSubmit) {
          toast.success("Form submitted successfully!");
          setIsFormVisible(false);
        }
      } catch (error) {
        console.error(error);
        setError('Failed to save progress. Please try again.');
        toast.error('Failed to save progress. Please try again.');
      }
    } else {
      const updatedMemberSteps = [...memberSteps];
      updatedMemberSteps[memberIndex] = updatedMemberSteps[memberIndex].map((s) => {
        if (s.step === 1) {
          return {
            ...s,
            title: s.title,
            status: isFinalSubmit ? "submitted" : answeredCount >= 0 ? "in-progress" : "pending",
            progress: answeredCount,
            answers: data.answers,
            comments: data.comments,
            file: data.file || s.file,
            files: data.files || s.files || [],
            signatureData: isFinalSubmit ? data.signatureData : null,
          };
        }
        if (s.step === 2 && isFinalSubmit) {
          return {
            ...s,
            title: s.title,
            status: "in-progress",
          };
        }
        return s;
      });

      setMemberSteps(updatedMemberSteps);

      // Save the updated member steps to the database
      const token = localStorage.getItem("token");
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/update-member-steps`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            memberIndex,
            steps: updatedMemberSteps[memberIndex],
            signatureData: isFinalSubmit ? data.signatureData : null
          }),
        });

        if (!response.ok) throw new Error("Failed to update member steps");

        if (isFinalSubmit) {
          toast.success(`Member ${memberIndex + 1} form submitted successfully!`);
          setMemberFormVisible(prev => ({ ...prev, [memberIndex]: false }));
        }
      } catch (error) {
        console.error(error);
        setError('Failed to save member progress. Please try again.');
        toast.error('Failed to save member progress. Please try again.');
      }
    }
  };

  const handleFeedbackSubmit = async (rating, comment) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.warning("Please log in to submit feedback.");
      return;
    }

    if (!userData || !userData._id) {
      toast.warning("User data not available. Please refresh the page.");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: userData._id,
          rating,
          comment: comment || '',
          memberIndex: feedbackMemberIndex !== -1 ? feedbackMemberIndex : null
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.message || `Failed to submit feedback: ${response.status}`);
      }

      setUserData(prev => {
        const updated = { ...prev };
        const fb = {
          rating,
          comment: comment || '',
          submittedAt: new Date()
        };

        if (feedbackMemberIndex !== -1 && updated.members && updated.members[feedbackMemberIndex]) {
          updated.members[feedbackMemberIndex].feedback = fb;
        } else {
          updated.feedback = fb;
        }
        return updated;
      });
      setShowFeedbackModal(false);
      setShowReferralModal(true);
      // Refresh user data to check if another member also needs feedback
      await fetchUserData();
    } catch (error) {
      console.error('Feedback submission error:', error);
      toast.error(`Failed to submit feedback: ${error.message || 'Please try again.'}`);
    }
  };

  const handleStep3Submit = async (submittedData, memberType = "primary", memberIndex = 0) => {
    const token = localStorage.getItem("token");
    setIsSubmittingStep3(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-step3-custom-table`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: userData._id,
            memberIndex: memberType === 'member' ? memberIndex : null,
            submittedData
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit Step 3 form");
      }

      const data = await response.json();

      // Close modal
      if (memberType === "primary") {
        setShowStep3Modal(false);
      } else {
        setShowMemberStep3Modal(prev => ({ ...prev, [memberIndex]: false }));
      }

      // Optimistically update steps status to 'submitted' for the UI
      if (memberType === "primary") {
        setSteps(prev => prev.map(s => s.step === 3 ? { ...s, status: 'submitted' } : s));
      } else {
        setMemberSteps(prev => {
          const updated = [...prev];
          if (updated[memberIndex]) {
            updated[memberIndex] = updated[memberIndex].map(s => s.step === 3 ? { ...s, status: 'submitted' } : s);
          }
          return updated;
        });
      }

      // Briefly wait to show success before refreshing data
      setTimeout(() => {
        fetchUserData();
      }, 500);

      toast.success('Step 3 form submitted successfully! The admin will review your submission.');
    } catch (error) {
      console.error('Error submitting Step 3 form:', error);
      toast.error('Failed to submit Step 3 form: ' + error.message);
    } finally {
      setIsSubmittingStep3(false);
    }
  };

  // Handle signed document submission (Step 4)
  const handleSignedDocSubmit = async (signedPdfBlob) => {
    setIsSubmittingSignature(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('signedPdf', signedPdfBlob, 'signed_document.pdf');
      formData.append('userId', userData._id);
      formData.append('memberIndex', signingMemberIndex);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-signed-document`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to submit signed document');
      }

      setShowSigningModal(false);
      await fetchUserData();
      toast.success('Document signed and submitted successfully! The signed copy has been saved to your Documents.');
    } catch (error) {
      console.error('Error submitting signed document:', error);
      toast.error('Failed to submit: ' + error.message);
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeaderSkeleton />
        <StepCardsSkeleton count={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center">
        <div className="card p-8 text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 transition-all duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Instruction Banner */}
      <div
        id="tax-filing-header"
        className="relative overflow-hidden rounded-2xl px-4 py-2 shadow-lg opacity-0 animate-fade-in-up"
        style={{
          background: `linear-gradient(135deg, var(--primary-600), var(--primary-700))`,
          animationDelay: '0ms',
          animationFillMode: 'forwards'
        }}
      >

        <div className="relative flex items-center justify-between gap-3 text-white">
          <div className="flex items-center gap-3 flex-1 overflow-hidden">
            <span className="shrink-0 bg-white/10 p-1.5 rounded-lg flex items-center justify-center">
              <FaClipboardCheck className="w-5 h-5 text-white" />
            </span>
            <marquee
              className="text-sm md:text-base font-medium"
              behavior="scroll"
              direction="left"
              scrollamount="5"
            >
              {taxFilingMeta.instructionBanner || 'Start with Upload Documents ➡️ Review & Confirm Checklist ➡️ Review Draft Tax Return ➡️ Return sent for Filing | 💬 Use Chat with Us to communicate.'}
            </marquee>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsHistorySidebarOpen(true)}
              className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-primary-600 hover:bg-gray-50 transition-all duration-200 text-xs font-bold shadow-lg active:scale-95 whitespace-nowrap cursor-pointer"
            >
              <FaHistory className="w-3.5 h-3.5" />
              <span>Filing History</span>
            </button>
            <button
              onClick={() => setRunTour(true)}
              className="hidden shrink-0 flex items-center gap-2 px-4 py-2 rounded-full bg-white text-primary-600 hover:bg-gray-100 transition-all duration-200 text-xs font-bold shadow-lg active:scale-95 whitespace-nowrap"
            >
              <LuCircleHelp className="w-4 h-4" />
              {taxFilingMeta.tourButtonLabel || 'Start Interactive Tour'}
            </button>
          </div>
        </div>
      </div>

      {/* Primary Form Header */}
      <div className="card px-5 py-4 min-h-[72px] flex items-center opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <h1 className="text-xl font-bold text-dark-800 dark:text-white shrink-0">
              {getFirstName(userData?.name)}
            </h1>
            <span className="px-3 py-1 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-sm font-semibold">
              {getFormBadgeLabel(assignedForm, taxFilingMeta)}
            </span>
          </div>
          {renderTaxDateInfo(userData?.taxYearEndDate, userData?.dueDate, assignedForm)}
        </div>
      </div>

      {/* Locked Banner for Primary */}
      {taxFilingLocked && (
        <div className="card p-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white opacity-0 animate-fade-in-up" style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <FaLock className="w-6 h-6" />
              <div>
                <h3 className="font-bold text-lg">{taxFilingMeta.lockedBannerTitle}</h3>
                <p className="text-sm opacity-90">{taxFilingMeta.lockedBannerText}</p>
              </div>
            </div>
            <button
              onClick={() => handlePayToUnlock("primary", null)}
              className="px-6 py-2.5 bg-white text-orange-600 font-semibold rounded-xl hover:bg-gray-100 transition-all duration-200 shadow-lg"
            >
              {taxFilingMeta.lockedPayButtonLabel || 'Pay $150 to Unlock'}
            </button>
          </div>
        </div>
      )}

      {/* WIP-Unpaid Banner for Primary */}
      {userData?.paymentStatus === 'wip-unpaid' && !taxFilingLocked && (
        <div className="card p-4 bg-gradient-to-r from-yellow-500 to-amber-600 text-white opacity-0 animate-fade-in-up" style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <FaClock className="w-6 h-6" />
              <div>
                <h3 className="font-bold text-lg">{taxFilingMeta.wipBannerTitle}</h3>
                <p className="text-sm opacity-90">{taxFilingMeta.wipBannerText}</p>
              </div>
            </div>
            <button
              onClick={() => handlePayToUnlock("primary", null)}
              className="px-6 py-2.5 bg-white text-yellow-600 font-semibold rounded-xl hover:bg-gray-100 transition-all duration-200 shadow-lg whitespace-nowrap"
            >
              {taxFilingMeta.wipPayButtonLabel || 'Complete Payment'}
            </button>
          </div>
        </div>
      )}

      {/* Primary Steps Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" style={{ minHeight: '200px' }}>
        {steps.map((s, index) => {
          const stepMeta = getStepCardMeta(taxFilingMeta.stepCards, s.step, s.status);
          return (
          <div
            key={`wrapper-${s.step}`}
            className="opacity-0 animate-fade-in-up h-full"
            style={{ animationDelay: `${200 + index * 100}ms`, animationFillMode: 'forwards' }}
          >
            <StepCard
              step={s.step}
              title={s.title}
              status={s.status}
              progress={s.progress}
              answers={s.answers}
              totalQuestions={totalQuestions}
              instruction={stepMeta.instruction}
              progressMessage={stepMeta.progressMessage}
              onClick={() => handleStepClick(s.step)}
              isLocked={taxFilingLocked}
              onPayClick={() => handlePayToUnlock("primary", null)}
              completedAt={s.completedAt}
            />
          </div>
        );})}
      </div>

      {/* Primary Member Tax Filing Table – visible when admin has saved a table */}
      {userData?.taxFilingSummaryTable?.columns &&
        userData.taxFilingSummaryTable.columns.length > 0 && (
          <div className="card p-4 mt-4 opacity-0 animate-fade-in-up bg-white shadow-lg border border-gray-200" style={{ animationDelay: '260ms', animationFillMode: 'forwards', borderRadius: '1rem' }}>
            <h3 className="text-lg font-bold text-dark-800 dark:text-white mb-2">
              Tax Filing Summary – Primary Member
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              This table is configured by the BooksComply team and becomes available after the Tax Filing steps are completed.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                <thead className="bg-gray-100">
                  <tr>
                    {userData.taxFilingSummaryTable.columns.map((col, idx) => (
                      <th
                        key={idx}
                        className="px-4 py-3 text-left text-xs tracking-wider font-semibold uppercase text-gray-600 border-b border-gray-200"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(userData.taxFilingSummaryTable.rows || []).map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                    >
                      {userData.taxFilingSummaryTable.columns.map((_, colIndex) => (
                        <td
                          key={colIndex}
                          className="px-4 py-3 text-sm text-dark-800 border-b border-gray-100"
                        >
                          {row && row[colIndex] ? row[colIndex] : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {(!userData.taxFilingSummaryTable.rows ||
                    userData.taxFilingSummaryTable.rows.length === 0) && (
                      <tr>
                        <td
                          colSpan={userData.taxFilingSummaryTable.columns.length}
                          className="px-4 py-4 text-center text-gray-500"
                        >
                          No data available in this table.
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
          </div>
        )}



      {isFormVisible && (
        <QuestionsForm
          onClose={() => setIsFormVisible(false)}
          onSubmit={(data, isFinal) => handleFormSubmit(data, isFinal, "primary")}
          initialAnswers={steps[0].answers}
          initialComments={steps[0].comments}
          initialFiles={(() => {
            console.log('Primary step files:', steps[0].files);
            return steps[0].files || [];
          })()}
          isSubmitted={steps[0].status === "submitted"}
          formNumber={assignedForm}
        />
      )}



      {/* Render member forms */}
      {userData?.members?.map((member, index) => (
        <React.Fragment key={`member-${index}`}>
          {/* Member Form Header */}
          <div className="card px-5 py-4 min-h-[72px] flex items-center mt-8">
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-3 min-w-0">
                <h2 className="text-xl font-bold text-dark-800 dark:text-white shrink-0">
                  {getFirstName(member.name) || `Member ${index + 1}`}
                </h2>
                <span className="px-3 py-1 rounded-lg bg-secondary-100 dark:bg-secondary-900/30 text-secondary-700 dark:text-secondary-400 text-sm font-semibold">
                  {getFormBadgeLabel(memberForms[index] || '1040', taxFilingMeta)}
                </span>
              </div>
              {renderTaxDateInfo(
                member?.taxYearEndDate,
                member?.dueDate,
                memberForms[index] || '1040'
              )}
            </div>
          </div>

          {/* Member Locked Banner */}
          {memberTaxFilingLocked[index] && (
            <div className="card p-4 mt-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <FaLock className="w-5 h-5" />
                  <div>
                    <h3 className="font-bold">{taxFilingMeta.lockedBannerTitle}</h3>
                    <p className="text-sm opacity-90">{taxFilingMeta.lockedBannerText}</p>
                  </div>
                </div>
                <button
                  onClick={() => handlePayToUnlock("member", index)}
                  className="px-5 py-2 bg-white text-orange-600 font-semibold rounded-xl hover:bg-gray-100 transition-all duration-200 shadow-lg"
                >
                  {taxFilingMeta.lockedPayButtonLabel || 'Pay $150 to Unlock'}
                </button>
              </div>
            </div>
          )}

          {/* Member WIP-Unpaid Banner */}
          {member.paymentStatus === 'wip-unpaid' && !memberTaxFilingLocked[index] && (
            <div className="card p-4 mt-4 bg-gradient-to-r from-yellow-500 to-amber-600 text-white">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <FaClock className="w-5 h-5" />
                  <div>
                    <h3 className="font-bold">{taxFilingMeta.wipBannerTitle}</h3>
                    <p className="text-sm opacity-90">{taxFilingMeta.wipBannerText}</p>
                  </div>
                </div>
                <button
                  onClick={() => handlePayToUnlock("member", index)}
                  className="px-5 py-2 bg-white text-yellow-600 font-semibold rounded-xl hover:bg-gray-100 transition-all duration-200 shadow-lg whitespace-nowrap"
                >
                  {taxFilingMeta.wipPayButtonLabel || 'Complete Payment'}
                </button>
              </div>
            </div>
          )}

          {/* Member Steps Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-4" style={{ minHeight: '200px' }}>
            {memberSteps[index]?.map((s) => {
              const stepMeta = getStepCardMeta(taxFilingMeta.stepCards, s.step, s.status);
              return (
              <div key={`member-${index}-${s.step}`} className="h-full">
                <StepCard
                  step={s.step}
                  title={s.title}
                  status={s.status}
                  progress={s.progress}
                  answers={s.answers}
                  totalQuestions={memberTotalQuestions[index] || 0}
                  instruction={stepMeta.instruction}
                  progressMessage={stepMeta.progressMessage}
                  onClick={() => handleStepClick(s.step, "member", index)}
                  isLocked={memberTaxFilingLocked[index]}
                  onPayClick={() => handlePayToUnlock("member", index)}
                  completedAt={s.completedAt}
                />
              </div>
            );})}
          </div>

          {/* Member Tax Filing Table – visible when admin has saved a table */}
          {member.taxFilingSummaryTable?.columns &&
            member.taxFilingSummaryTable.columns.length > 0 && (
              <div className="card p-4 mt-4">
                <h4 className="section-title mb-2">
                  Tax Filing Summary – {member.name || `Member ${index + 1}`}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  This table is configured by the BooksComply team and becomes available after all Tax Filing steps for this member are completed.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs border border-gray-200 dark:border-dark-700 rounded-xl overflow-hidden">
                    <thead className="bg-gray-100 dark:bg-dark-700">
                      <tr>
                        {member.taxFilingSummaryTable.columns.map((col, idx) => (
                          <th
                            key={idx}
                            className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-dark-700"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(member.taxFilingSummaryTable.rows || []).map((row, rowIdx) => (
                        <tr
                          key={rowIdx}
                          className="odd:bg-white even:bg-gray-50 dark:odd:bg-dark-800 dark:even:bg-dark-700/60"
                        >
                          {member.taxFilingSummaryTable.columns.map((_, colIdx) => (
                            <td
                              key={colIdx}
                              className="px-3 py-2 text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-dark-700"
                            >
                              {row && row[colIdx] ? row[colIdx] : '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {(!member.taxFilingSummaryTable.rows ||
                        member.taxFilingSummaryTable.rows.length === 0) && (
                          <tr>
                            <td
                              colSpan={member.taxFilingSummaryTable.columns.length}
                              className="px-3 py-3 text-center text-gray-400 dark:text-gray-500"
                            >
                              No data available in this table.
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}



          {/* Bundle Activation Message */}
          {userData?.isBundlePurchased && (
            <div className="mt-4">
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-500 text-white font-semibold shadow-lg shadow-green-500/30">
                <span className="text-lg">✅</span>
                <span>Quarterly Tax Payment Activated</span>
              </div>
            </div>
          )}

          {memberFormVisible[index] && (
            <QuestionsForm
              onClose={() => setMemberFormVisible(prev => ({ ...prev, [index]: false }))}
              onSubmit={(data, isFinal) => handleFormSubmit(data, isFinal, "member", index)}
              initialAnswers={memberSteps[index]?.[0]?.answers || {}}
              initialComments={memberSteps[index]?.[0]?.comments || {}}
              initialFiles={(() => {
                console.log(`Member ${index} step files:`, memberSteps[index]?.[0]?.files);
                return memberSteps[index]?.[0]?.files || [];
              })()}
              isSubmitted={memberSteps[index]?.[0]?.status === "submitted"}
              formNumber={memberForms[index] || "1040"}
              memberType="member"
              memberIndex={index}
              memberName={member.name}
            />
          )}
        </React.Fragment>
      ))}

      {showFeedbackModal && (
        <FeedbackModal
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
        />
      )}

      {showReferralModal && (
        <ReferralRewardModal
          isOpen={showReferralModal}
          onClose={() => setShowReferralModal(false)}
        />
      )}

      {/* Step 3 Client Pending Modal - Primary */}
      {showStep3Modal && (
        <Step3ClientPendingModal
          isOpen={showStep3Modal}
          onClose={() => setShowStep3Modal(false)}
          customTable={step3CustomTable}
          onSubmit={(data) => handleStep3Submit(data, "primary", 0)}
          isSubmitted={step3CustomTable?.userSubmittedData && step3CustomTable.userSubmittedData.length > 0}
          isSubmitting={isSubmittingStep3}
        />
      )}

      {/* Step 3 Client Pending Modal - Members */}
      {userData?.members?.map((member, index) => (
        showMemberStep3Modal[index] && (
          <Step3ClientPendingModal
            key={`member-step3-${index}`}
            isOpen={showMemberStep3Modal[index]}
            onClose={() => setShowMemberStep3Modal(prev => ({ ...prev, [index]: false }))}
            customTable={memberStep3CustomTable[index]}
            onSubmit={(data) => handleStep3Submit(data, "member", index)}
            isSubmitted={memberStep3CustomTable[index]?.userSubmittedData && memberStep3CustomTable[index].userSubmittedData.length > 0}
            isSubmitting={isSubmittingStep3}
          />
        )
      ))}

      {/* Step 4 Document Signing Modal */}
      <DocumentSigningModal
        isOpen={showSigningModal}
        onClose={() => setShowSigningModal(false)}
        documentUrl={signingDocUrl}
        signatureBoxes={signingBoxes}
        onSubmit={handleSignedDocSubmit}
        isSubmitting={isSubmittingSignature}
      />

      {/* Tour Guide */}
      <TourGuide run={runTour} setRun={setRunTour} onFinish={handleTourFinish} />

      {/* Tax Filing History Slide-out Sidebar Overlay */}
      {isHistorySidebarOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex justify-end" onClick={() => setIsHistorySidebarOpen(false)}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/45 backdrop-blur-sm transition-opacity duration-300 animate-in fade-in" />
          
          {/* Sidebar Drawer Container */}
          <div 
            className="relative w-full max-w-lg bg-white dark:bg-dark-900 h-full shadow-2xl flex flex-col z-10 overflow-hidden border-l border-gray-100 dark:border-dark-800 animate-slide-in"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: 'slideInRight 0.3s ease-out forwards',
            }}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between bg-gray-50/50 dark:bg-dark-800/50">
              <div className="flex items-center gap-2">
                <FaHistory className="w-5 h-5 text-primary-500" />
                <h3 className="text-lg font-bold text-dark-800 dark:text-white">Tax Filing History</h3>
              </div>
              <button 
                onClick={() => setIsHistorySidebarOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-xl transition-all cursor-pointer"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 p-5 overflow-y-auto space-y-6 custom-scrollbar bg-white dark:bg-dark-950">
              {(!taxFilingHistory || taxFilingHistory.length === 0) && 
               (!userData?.members?.some((_, idx) => memberTaxFilingHistory[idx]?.length > 0)) ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-gray-500">
                  <FaHistory className="w-12 h-12 mb-3 opacity-30 animate-pulse" />
                  <p className="text-sm font-medium">No tax filing history found</p>
                </div>
              ) : (
                <>
                  {/* Primary Member History */}
                  {taxFilingHistory && taxFilingHistory.length > 0 && (
                    <div className="space-y-6">
                      <h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                        Primary Member - {getFirstName(userData?.name)}
                      </h4>
                      {taxFilingHistory.map((historyItem, historyIndex) => (
                        <div key={`sidebar-primary-history-${historyIndex}`} className="mb-6">
                          <div className="border border-gray-200 dark:border-gray-700 rounded-t-xl px-4 py-3 min-h-[64px] flex items-center bg-gray-50 dark:bg-gray-800/50">
                            <div className="flex w-full flex-col gap-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <h4 className="text-base font-bold text-dark-800 dark:text-white shrink-0">
                                  {getFirstName(userData?.name)}
                                </h4>
                                <span className="px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-semibold">
                                  Form {historyItem.assignedForm || assignedForm}
                                </span>
                              </div>
                              <div className="mt-2 space-y-1 text-xs text-gray-500">
                                {historyItem.taxYearEndDate && (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="w-1.5 h-1.5 rounded-full bg-secondary-500 shrink-0" />
                                    <span>
                                      Tax Year End: <strong className="text-dark-700 dark:text-gray-300">{formatDate(historyItem.taxYearEndDate)}</strong>
                                    </span>
                                  </div>
                                )}
                                {historyItem.dueDate && (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0" />
                                    <span>
                                      Due Date: <strong className="text-dark-700 dark:text-gray-300">{formatDate(historyItem.dueDate)}</strong>{' '}
                                      <span className="text-primary-600 dark:text-primary-400 font-semibold">
                                        {getDueDateSuffix(historyItem.assignedForm || assignedForm)}
                                      </span>
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-xl px-4 pb-4 pt-4 -mt-px bg-gray-50 dark:bg-gray-800/50">
                            <div className="grid grid-cols-2 gap-2">
                              {historyItem.steps?.map((s) => (
                                <div
                                  key={`sidebar-history-${historyIndex}-step-${s.step}`}
                                  className="p-3 rounded-lg bg-green-500 text-white text-center flex flex-col justify-center min-h-[72px]"
                                >
                                  <div className="flex items-center justify-center gap-1 mb-0.5">
                                    <div className="text-[10px] font-bold opacity-80">Step {s.step}</div>
                                    {(historyItem.assignedForm || assignedForm) && (
                                      <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-white/20">
                                        {historyItem.assignedForm || assignedForm}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px] font-semibold leading-tight my-0.5">{s.title}</div>
                                  <div className="text-[9px] opacity-75">✓ Completed</div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Feedback Rating */}
                            {historyItem.feedback?.rating && (
                              <div className="mt-3.5 text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1.5 font-medium">
                                <span>Feedback:</span>
                                <span className="text-amber-500 text-sm">{'⭐'.repeat(historyItem.feedback.rating)}</span>
                                <span className="text-gray-400">({historyItem.feedback.rating}/5)</span>
                              </div>
                            )}

                            {/* Snapshot summary table for this history cycle, if available */}
                            {historyItem.summaryTable?.columns && historyItem.summaryTable.columns.length > 0 && (
                              <div className="mt-4">
                                <h5 className="text-[11px] font-bold text-dark-800 dark:text-gray-100 mb-2">
                                  Summary Table for this cycle
                                </h5>
                                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-700">
                                  <table className="min-w-full text-[10px] border-collapse">
                                    <thead className="bg-gray-105 dark:bg-dark-700 text-gray-700 dark:text-gray-300">
                                      <tr>
                                        {historyItem.summaryTable.columns.map((col, idx) => (
                                          <th key={idx} className="px-2 py-1.5 text-left font-bold border-b border-gray-200 dark:border-dark-700">{col}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(historyItem.summaryTable.rows || []).map((row, rowIdx) => (
                                        <tr key={rowIdx} className="bg-white dark:bg-dark-800 odd:bg-white even:bg-gray-50 dark:odd:bg-dark-800 dark:even:bg-dark-700/65 border-b border-gray-100 dark:border-dark-750">
                                          {historyItem.summaryTable.columns.map((_, colIdx) => (
                                            <td key={colIdx} className="px-2 py-1 text-gray-800 dark:text-gray-200">{row && row[colIdx] ? row[colIdx] : '-'}</td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Members History */}
                  {userData?.members?.map((member, index) => {
                    const mHistory = memberTaxFilingHistory[index];
                    if (!mHistory || mHistory.length === 0) return null;
                    return (
                      <div key={`sidebar-member-history-section-${index}`} className="space-y-4 pt-6 border-t border-gray-200 dark:border-dark-750">
                        <h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-secondary-500" />
                          Member: {member.name}
                        </h4>
                        {mHistory.map((historyItem, historyIndex) => (
                          <div key={`sidebar-member-${index}-history-${historyIndex}`} className="mb-6">
                            <div className="border border-gray-200 dark:border-gray-700 rounded-t-xl px-4 py-3 min-h-[64px] flex items-center bg-gray-50 dark:bg-gray-800/50">
                              <div className="flex w-full flex-col gap-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h4 className="text-base font-bold text-dark-800 dark:text-white shrink-0">
                                    {getFirstName(member.name) || `Member ${index + 1}`}
                                  </h4>
                                  <span className="px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-semibold">
                                    Form {historyItem.assignedForm}
                                  </span>
                                </div>
                                <div className="mt-2 space-y-1 text-xs text-gray-500">
                                  {historyItem.taxYearEndDate && (
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="w-1.5 h-1.5 rounded-full bg-secondary-500 shrink-0" />
                                      <span>
                                        Tax Year End: <strong className="text-dark-700 dark:text-gray-300">{formatDate(historyItem.taxYearEndDate)}</strong>
                                      </span>
                                    </div>
                                  )}
                                  {historyItem.dueDate && (
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0" />
                                      <span>
                                        Due Date: <strong className="text-dark-700 dark:text-gray-300">{formatDate(historyItem.dueDate)}</strong>{' '}
                                        <span className="text-primary-600 dark:text-primary-400 font-semibold">
                                          {getDueDateSuffix(historyItem.assignedForm)}
                                        </span>
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-xl px-4 pb-4 pt-4 -mt-px bg-gray-50 dark:bg-gray-800/50">
                              <div className="grid grid-cols-2 gap-2">
                                {historyItem.steps?.map((s) => (
                                  <div
                                    key={`sidebar-member-${index}-history-${historyIndex}-step-${s.step}`}
                                    className="p-3 rounded-lg bg-green-500 text-white text-center flex flex-col justify-center min-h-[72px]"
                                  >
                                    <div className="flex items-center justify-center gap-1 mb-0.5">
                                      <div className="text-[10px] font-bold opacity-80">Step {s.step}</div>
                                      {historyItem.assignedForm && (
                                        <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-white/20">
                                          {historyItem.assignedForm}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[11px] font-semibold leading-tight my-0.5">{s.title}</div>
                                    <div className="text-[9px] opacity-75">✓ Completed</div>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Member Feedback Rating */}
                              {historyItem.feedback?.rating && (
                                <div className="mt-3.5 text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1.5 font-medium">
                                  <span>Feedback:</span>
                                  <span className="text-amber-500 text-sm">{'⭐'.repeat(historyItem.feedback.rating)}</span>
                                  <span className="text-gray-400">({historyItem.feedback.rating}/5)</span>
                                </div>
                              )}

                              {/* Member snapshot summary table */}
                              {historyItem.summaryTable?.columns && historyItem.summaryTable.columns.length > 0 && (
                                <div className="mt-4">
                                  <h5 className="text-[11px] font-bold text-dark-800 dark:text-gray-100 mb-2">
                                    Summary Table for this cycle
                                  </h5>
                                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-700">
                                    <table className="min-w-full text-[10px] border-collapse">
                                      <thead className="bg-gray-105 dark:bg-dark-700 text-gray-700 dark:text-gray-300">
                                        <tr>
                                          {historyItem.summaryTable.columns.map((col, idx) => (
                                            <th key={idx} className="px-2 py-1.5 text-left font-bold border-b border-gray-200 dark:border-dark-700">{col}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(historyItem.summaryTable.rows || []).map((row, rowIdx) => (
                                          <tr key={rowIdx} className="bg-white dark:bg-dark-800 odd:bg-white even:bg-gray-50 dark:odd:bg-dark-800 dark:even:bg-dark-700/65 border-b border-gray-100 dark:border-dark-750">
                                            {historyItem.summaryTable.columns.map((_, colIdx) => (
                                              <td key={colIdx} className="px-2 py-1 text-gray-800 dark:text-gray-200">{row && row[colIdx] ? row[colIdx] : '-'}</td>
                                            ))}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default StepTracker;